import React, { useState } from 'react';
import { Typography, Alert, Card, Button, Space, Modal, Statistic, Row, Col } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import MolStarViewer from '../protein/structure/MolStarViewer';

const { Text } = Typography;

interface AntibodyStructureResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const AntibodyStructureResult: React.FC<AntibodyStructureResultProps> = ({ 
  taskResult, 
  data, 
  title = "抗体结构预测结果", 
  showTitle = true 
}) => {
  const [viewerVisible, setViewerVisible] = useState(false);

  // 提取抗体结构结果
  const getStructureResult = () => {
    // 格式1: 统一格式数据：从results[0].result中提取实际数据
    if (taskResult?.result?.results && Array.isArray(taskResult.result.results) && taskResult.result.results.length > 0) {
      const structureData = taskResult.result.results[0].result;
      if (structureData && (structureData.pdb_data || structureData.pdb_content)) {
        return {
          ...structureData,
          pdb_content: structureData.pdb_data || structureData.pdb_content
        };
      }
    }
    
    // 格式2: 直接的结果数据
    if (taskResult?.pdb_data || taskResult?.pdb_content) {
      return {
        ...taskResult,
        pdb_content: taskResult.pdb_data || taskResult.pdb_content
      };
    }
    
    // 格式3: data参数
    if (data) {
      return {
        ...data,
        pdb_content: data.pdb_data || data.pdb_content
      };
    }
    
    return null;
  };

  const result = getStructureResult();

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (!result) {
    return (
      <Alert
        type="info"
        message="无抗体结构预测结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
        </div>
      )}

      <Card>
        {result.structure_info && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic title="重链长度" value={result.structure_info.heavy_chain_length || 0} />
            </Col>
            <Col span={8}>
              <Statistic title="轻链长度" value={result.structure_info.light_chain_length || 0} />
            </Col>
            <Col span={8}>
              <Statistic title="总原子数" value={result.structure_info.total_atoms || 0} />
            </Col>
          </Row>
        )}

        <Space>
          {result.pdb_content && (
            <>
              <Button 
                type="primary" 
                icon={<EyeOutlined />}
                onClick={() => setViewerVisible(true)}
              >
                查看3D结构
              </Button>
              <Button 
                icon={<DownloadOutlined />}
                onClick={() => {
                  const blob = new Blob([result.pdb_content], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'antibody_structure.pdb';
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                下载PDB
              </Button>
            </>
          )}
        </Space>
      </Card>

      <Modal
        title="抗体结构预测"
        open={viewerVisible}
        onCancel={() => setViewerVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {result.pdb_content && (
          <div style={{ height: 600 }}>
            <MolStarViewer 
              pdbData={result.pdb_content} 
              title="抗体结构"
              height={580}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AntibodyStructureResult;
